import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Button,
	Row,
	Col,
	DatePicker,
	Select,
	Statistic,
	Form,
	Input,
	notification,
	InputNumber,
} from "antd";
import Layout from "../layout/Layout";
import { CustomSpinner, CustomTable } from "../utils/CustomComponents";
import dayjs from "dayjs";
import { cityList } from "../utils/CityList";
const { RangePicker } = DatePicker;

const { Text } = Typography;

const JobSalesReport = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [plants, setPlants] = useState([]);
	const [customers, setCustomers] = useState([]);
	const [sellers, setSellers] = useState([]);
	const [cities, setCities] = useState([]);
	const [psis, setPsis] = useState([]);
	const [jobList, setJobList] = useState([]);
	const [disabled, setDisabled] = useState(true);

	const [form] = Form.useForm();

	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Job ID",
			dataIndex: "jobId",
			key: "jobId",
			render: (value, record) => {
				return (
					<Text
						strong
						onClick={() => history.push(`/job/${record.key}`)}
						style={{ cursor: "pointer" }}
					>
						{value}
					</Text>
				);
			},
		},
		{
			title: "Client",
			dataIndex: "client",
			key: "client",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Load YD3",
			dataIndex: "loadYD3",
			key: "loadYD3",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "PSI",
			dataIndex: "psi",
			key: "psi",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Customer Ship Address",
			dataIndex: "customerShipAddress",
			key: "customerShipAddress",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Unit Price",
			dataIndex: "unitPrice",
			key: "unitPrice",
			render: (value) => {
				return (
					<Statistic
						value={value}
						valueStyle={{
							fontSize: 14,
						}}
						prefix="$"
						precision={2}
					/>
				);
			},
		},
		{
			title: "City",
			dataIndex: "city",
			key: "city",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
	];

	useEffect(() => {
		getValues();
	}, []);

	const getValues = async () => {
		await Promise.all([
			getPlants(),
			getCustomers(),
			getSellers(),
			getCities(),
			getPsi(),
		]).then(() => {
			setLoading(false);
			setDisabled(false);
		});
	};

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			data.payload.map((item) => {
				list.push({
					value: item._id,
					label: item.name,
				});
			});
			setPlants(list);
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get plants");
			console.error(err.message);
			return null;
		}
	};

	const getCustomers = async () => {
		try {
			const url = api + "/customer/list/options/57Concrete";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item) => {
					list.push({
						label: `${item.customerNumber} ${item.name}`,
						value: item._id,
					});
				});
				setCustomers(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get customers");
			console.error(err.message);
			return null;
		}
	};

	const getSellers = async () => {
		try {
			const url = api + "/user/list/57Concrete/Seller";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item) => {
					list.push({
						label: `${item.name}`,
						value: item._id,
					});
				});
				setSellers(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get sellers");
			console.error(err.message);
			return null;
		}
	};

	const getCities = () => {
		setCities(
			cityList.map((city) => ({
				label: city.label,
				value: city.label,
			})),
		);
	};

	const getPsi = async () => {
		const url = api + "/psi/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			data.map((item) => {
				list.push({
					value: item._id,
					label: item.psi,
				});
			});
			setPsis(list);
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get PSIs");
			console.error(err.message);
			return null;
		}
	};

	const getJobs = async (values) => {
		try {
			setLoadingTable(true);
			setDisabled(true);

			const { dates, customer, seller, psi, plant, unitPrice, city } = values;

			const formattedDateStart = dayjs(dates[0]);
			const start = `${formattedDateStart.year()}-${
				formattedDateStart.month() + 1
			}-${formattedDateStart.date()}`;
			const formattedDateEnd = dayjs(dates[1]);
			const end = `${formattedDateEnd.year()}-${
				formattedDateEnd.month() + 1
			}-${formattedDateEnd.date()}`;

			const url =
				api +
				`/job/list/prices?startDate=${start}&endDate=${end}&plant=${plant}&customer=${customer}&seller=${seller}&psi=${psi}&unitPrice=${unitPrice}&city=${city}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);

			if (data.success) {
				const payload = data.payload;
				setJobList(
					payload.map((item) => ({
						key: item._id,
						date: dayjs(item.scheduledDate).format("MM/DD/YYYY"),
						jobId: item.number,
						client: item.customer?.name || "N/A",
						seller: item.seller?.name || "N/A",
						psi: item.psiId ? item.psiId.psi : item.psi,
						plant: item.plant?.name || "N/A",
						unitPrice: item.unitPrice,
						city: item.shipCity || "N/A",
						loadYD3: item.loadYD3 || 0,
						customerShipAddress: item.customerShipAddress || "N/A",
					})),
				);
			} else {
				openNotification("error", "Error", data.message);
			}

			setLoadingTable(false);
			setDisabled(false);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	const validateFields = (_, value) => {
		const values = form.getFieldsValue();
		const filledFields = Object.values(values).filter(
			(v) => v !== undefined && v !== "" && v !== null,
		);
		if (filledFields.length === 1) {
			openNotification(
				"warning",
				"Validation Error",
				"At least one field must be filled.",
			);
			return Promise.reject(new Error("At least one field must be filled."));
		}
		return Promise.resolve();
	};

	const onFinish = (values) => {
		delete values.validation;
		getJobs(values);
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const RenderHeader = () => {
		return (
			<Form
				form={form}
				layout="vertical"
				onFinish={onFinish}
				initialValues={{
					dates: [dayjs(), dayjs()],
				}}
			>
				<Row gutter={16} align={"middle"} justify={"start"}>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="dates">
							<RangePicker
								size="large"
								allowClear={false}
								format={"MM/DD/YYYY"}
								style={{ width: "100%" }}
								disabled={disabled}
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="customer">
							<Select
								size="large"
								options={customers}
								style={{ width: "100%" }}
								loading={disabled}
								disabled={disabled}
								allowClear
								placeholder="Select Customer"
								filterOption={filterOption}
								showSearch
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="seller">
							<Select
								size="large"
								options={sellers}
								style={{ width: "100%" }}
								loading={disabled}
								disabled={disabled}
								allowClear
								placeholder="Select Seller"
								filterOption={filterOption}
								showSearch
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="psi">
							<Select
								size="large"
								options={psis}
								style={{ width: "100%" }}
								loading={disabled}
								disabled={disabled}
								allowClear
								placeholder="Select PSI"
								filterOption={filterOption}
								showSearch
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="plant">
							<Select
								size="large"
								options={plants}
								style={{ width: "100%" }}
								loading={disabled}
								disabled={disabled}
								allowClear
								placeholder="Select Plant"
								filterOption={filterOption}
								showSearch
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="unitPrice">
							<InputNumber
								size="large"
								style={{ width: "100%" }}
								min={0}
								formatter={(value) =>
									`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, "")
								}
								parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
								placeholder="Unit Price"
								disabled={disabled}
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="city">
							<Select
								size="large"
								options={cities}
								style={{ width: "100%" }}
								loading={disabled}
								disabled={disabled}
								allowClear
								placeholder="Select City"
								filterOption={filterOption}
								showSearch
							/>
						</Form.Item>
					</Col>
					{/* Hidden validation trigger */}
					<Form.Item
						name="validation"
						noStyle
						dependencies={[
							"customer",
							"plant",
							"seller",
							"unitPrice",
							"city",
							"psi",
						]}
						rules={[{ validator: validateFields }]}
					>
						<Input type="hidden" />
					</Form.Item>
					<Col span={24} sm={12} xl={6}>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								size="large"
								style={{ width: "100%" }}
								disabled={disabled}
							>
								Filter
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		);
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<CustomTable
					columns={columns}
					data={jobList}
					loading={loadingTable}
					isScroll={true}
				/>
			</Flex>
		);
	};

	return (
		<Layout title="Job Price Report" description="Report">
			<Flex vertical gap={"large"}>
				<RenderHeader />
				{loading ? <CustomSpinner /> : <RenderTable />}
				{contextHolder}
			</Flex>
		</Layout>
	);
};

export default JobSalesReport;
